import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getListKeyPersonnels } from "actions/RFQ/Supplier/rfqActions";
import {
	SmartFieldQuestionnaireBase,
	filterSmartFieldOptionForSubmittedRFQ,
} from "./SmartFieldQuestionnaireBase";
import { useSmartFieldSelection } from "../hooks/useSmartFieldSelection";
import {
	KeyPersonnel,
	KeyPersonnelSkeleton,
} from "pages/RFQ/RFQ/CreateRFQ/PreviewRFQ/Questionnaire/_components/KeyPersonnel";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import { useCurrentCompanyId } from "hooks/auth";
import { LoadMoreCard } from "./LoadMoreCard";
import { AddNewCard } from "./AddNewCard";
import { AddNewKeyPersonnelModal } from "./AddNewKeyPersonnelModal";
import { KeyPersonnelTableView } from "./KeyPersonnelTableView";
import { RfxQuestion } from "newui/features/Rfx/types/returnable-schedule-question.type";
import type { ViewMode } from "./SmartFieldQuestionnaireBase";

interface KeyPersonnelItem {
	id: number;
	[key: string]: unknown;
	experience?: number;
	first_name?: string;
	last_name?: string;
	position?: string;
	projects_completed?: { name: string }[];
}

interface FilterOption {
	label: string;
	value: number | string;
}

interface PaginationInfo {
	current_page: number;
	has_more_pages: boolean;
	total: number;
}

interface KeyPersonnelPageResponse {
	filters?: FilterOption[];
	included_items?: KeyPersonnelItem[];
	key_personnels?: KeyPersonnelItem[];
	pagination?: PaginationInfo;
}

interface SmartFieldKeyPersonnelProps {
	name: string;
	[key: string]: unknown;
	answer?: {
		selectedKeyPersonnel?: KeyPersonnelItem[];
	};
	className?: string;
	disabled?: boolean;
	isClientPreview?: boolean;
	question: RfxQuestion;
	required?: boolean;
}

const keyPersonnelDummyData: KeyPersonnelItem = {
	id: 0,
	first_name: "Example",
	last_name: "Person Card",
	position: "Their Position",
	projects_completed: [
		{
			name: "Their Last Project",
		},
	],
};

export const SmartFieldKeyPersonnel: React.FC<SmartFieldKeyPersonnelProps> = ({
	className,
	question,
	required,
	isClientPreview,
	answer,
	...props
}) => {
	const [search, setSearch] = useState("");
	const [selectedFilter, setSelectedFilter] = useState<FilterOption[]>([]);
	const [showAddModal, setShowAddModal] = useState(false);
	const [viewMode, setViewMode] = useState<ViewMode>("card");
	const companyId = useCurrentCompanyId();
	const { rfxId, isClientView } = useRfxSubmissionData();
	const { value, error, touched, isSelected, selectHandler, isSubmitting } =
		useSmartFieldSelection(props, { shouldSubmit: true });

	// Capture initial selections so selected items always appear (stable ref — won't trigger refetch)
	const [initialIncludeIds] = useState<number[]>(() =>
		Array.isArray(value) ? value : value ? [value] : []
	);

	const queryParams = {
		search: search,
		filters: selectedFilter,
		replacement: 1,
		rfq_id: rfxId,
		...(initialIncludeIds.length > 0 && { include_ids: initialIncludeIds }),
	};

	const useAnswerData =
		(props?.disabled && answer?.selectedKeyPersonnel) || !rfxId || isClientView;

	const {
		data,
		isLoading,
		error: queryError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isRefetching,
		refetch,
	} = useInfiniteQuery<KeyPersonnelPageResponse>({
		queryKey: ["keyPersonnel", queryParams],
		queryFn: (context) =>
			getListKeyPersonnels({
				queryKey: context.queryKey,
				pageParam: (context.pageParam as number) ?? 1,
			}),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.pagination?.has_more_pages
				? lastPage.pagination.current_page + 1
				: undefined,
		enabled: !useAnswerData && !isClientPreview,
	});

	// Flatten paginated results
	const allPages = data?.pages ?? [];
	const paginatedPersonnel = allPages.flatMap(
		(page) => page.key_personnels ?? []
	);
	const includedItems = allPages[0]?.included_items ?? [];
	const filteringList = allPages[0]?.filters ?? [];

	// Merge paginated + included items (deduplicated)
	const mergedPersonnel = [...paginatedPersonnel];
	includedItems.forEach((item) => {
		if (!mergedPersonnel.some((p) => p.id === item.id)) {
			mergedPersonnel.push(item);
		}
	});

	const keyPersonnels =
		mergedPersonnel.length > 0
			? mergedPersonnel
			: (answer?.selectedKeyPersonnel ?? []);

	// Filter data for preview mode
	const filteredData = filterSmartFieldOptionForSubmittedRFQ(
		keyPersonnels,
		value,
		props?.disabled
	);

	const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
	const selectedData = keyPersonnels.filter((person) =>
		selectedIds.includes(person.id)
	);

	const pagination = allPages[allPages.length - 1]?.pagination;
	const remaining = pagination
		? pagination.total - paginatedPersonnel.length
		: 0;

	if (isClientPreview) {
		filteredData.push(keyPersonnelDummyData);
	}

	const handlePersonnelCreated = (newId: number): void => {
		selectHandler(newId);
	};

	const showAddNew = !isClientPreview && !isClientView;
	const hasItems = mergedPersonnel.length > 0;

	return (
		<SmartFieldQuestionnaireBase
			{...props}
			className={className}
			question={question}
			required={required}
			description="Please choose key personnel that might work on this project."
			searchPlaceholder="Search name, position or project"
			isLoading={isLoading}
			loadingMessage="Loading your key personnel..."
			filteringList={filteringList}
			selectedFilter={selectedFilter}
			setSelectedFilter={setSelectedFilter}
			setSearch={setSearch}
			error={error}
			touched={touched}
			type="key-personnel"
			selectedData={selectedData}
			showFilter={!props?.disabled}
			showSearch={!props?.disabled}
			isClientPreview={isClientPreview}
			onAddNew={showAddNew && hasItems ? () => setShowAddModal(true) : undefined}
			addNewLabel={showAddNew && hasItems ? "Add Key Personnel" : undefined}
			onRefresh={showAddNew ? () => refetch() : undefined}
			isRefreshing={isRefetching}
			viewMode={viewMode}
			onViewModeChange={setViewMode}
			tableContent={
				filteredData.length > 0 ? (
					<>
						<KeyPersonnelTableView
							items={filteredData}
							selectedIds={selectedIds}
							onSelect={selectHandler}
							companyId={companyId ?? undefined}
							isPreview={props?.disabled}
							isSubmitting={isSubmitting}
						/>
						{hasNextPage && !props?.disabled && (
							<div className="mt-3 flex justify-center">
								<button
									type="button"
									onClick={() => fetchNextPage()}
									disabled={isFetchingNextPage}
									className="flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 transition-colors hover:border-blue-400 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{isFetchingNextPage ? "Loading..." : `Load More (${remaining})`}
								</button>
							</div>
						)}
					</>
				) : undefined
			}
			emptyStateContent={showAddNew && !hasItems && !isLoading ? (
				<div className="mt-4 w-full">
					<p className="mb-2 text-sm italic text-gray-500">
						Please add new key personnel to your profile, to submit with your response. Once added here, they will be available for any future submissions.
					</p>
					<AddNewCard
						label="+ Add Key Personnel"
						onClick={() => setShowAddModal(true)}
						disabled={props.disabled}
					/>
				</div>
			) : undefined}
		>
			{isLoading ? (
				[...Array(3)].map((_, index) => <KeyPersonnelSkeleton key={index} />)
			) : queryError ? (
				<div className="text-red-500">
					Error loading personnel: {queryError.message || "Unknown error"}
				</div>
			) : filteredData.length === 0 && !showAddNew ? (
				<div className="italic text-gray-500">
					No personnel {useAnswerData ? "selected" : "found"}
				</div>
			) : (
				<>
					{filteredData.map((personnel) => (
						<KeyPersonnel
							key={personnel.id}
							companyId={companyId}
							values={personnel}
							rfqSubmission={true}
							selected={isSelected(personnel?.id)}
							selectHandler={() => selectHandler(personnel?.id)}
							isPreview={props?.disabled}
							isClientPreview={isClientPreview}
							supplierSmartField
							isSubmitting={isSubmitting}
						/>
					))}
					{hasNextPage && !props?.disabled && (
						<LoadMoreCard
							onClick={() => fetchNextPage()}
							isLoading={isFetchingNextPage}
							remaining={remaining}
						/>
					)}
				</>
			)}
			{showAddModal && (
				<AddNewKeyPersonnelModal
					isOpen={showAddModal}
					onClose={() => setShowAddModal(false)}
					onCreated={handlePersonnelCreated}
				/>
			)}
		</SmartFieldQuestionnaireBase>
	);
};
